import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Flex, Grid, SimpleGrid, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCirclePlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Checkbox,
  DatePicker,
  DECIMAL_PROPS,
  InputLabel,
  NumberInput,
  Select,
  TextInput,
  YesNoRadioButton,
} from "components";
import {
  BankCreditOptionsResponse,
  BankCreditResponse,
  BankCreditRequestChildResponse,
  ChildrenAddress,
} from "types";
import { AppUtils, DateUtils } from "utils";

interface BankCreditRequestPersonalChildrenIndividualProps {
  credit: BankCreditResponse;
  form: any;
  options: BankCreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
}

interface ChildObject {
  creditRequestId?: number;
  birthdate?: Date;
  craft?: boolean;
  craftAmount?: number;
  firstName?: string;
  lastName?: string;
  address?: ChildrenAddress;
  birthdateIncluded?: boolean;
}
const tempChildObject = {
  creditRequestId: undefined,
  birthdate: undefined,
  craft: undefined,
  craftAmount: undefined,
  firstName: undefined,
  lastName: undefined,
  address: undefined,
  birthdateIncluded: undefined,
} as ChildObject;

const propertyName = "children";

export const BankCreditRequestPersonalChildrenIndividual = ({
  credit,
  form,
  options,
  visibleFields,
  readOnly = false,
}: BankCreditRequestPersonalChildrenIndividualProps) => {
  const { t } = useTranslation();
  const [childrenVisibleFields, setChildrenVisibleFields] = useState<string[]>(
    AppUtils.getVisibleChildrenFields(credit[propertyName]),
  );
  const [supportsChildren, setSupportsChildren] = useState<boolean | undefined>(
    credit.supportsChildren,
  );

  const childrenForm = useForm<BankCreditRequestChildResponse[]>({
    mode: "uncontrolled",
    initialValues:
      credit[propertyName] && credit[propertyName].length > 0
        ? credit[propertyName].map((el) => ({
            ...el,
            birthdate: el.birthdate ? new Date(el.birthdate) : undefined,
          }))
        : [],
    onValuesChange(values, previous) {
      if (values[values.length - 1] !== tempChildObject) {
        updateCredit(values, previous);
      }
    },
  });

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    const supports = formValues.supportsChildren;
    if (!supports && childrenForm.values.length > 0) {
      childrenForm.setInitialValues([]);
      childrenForm.reset();
    }
    setSupportsChildren(supports);
  }, [form.getValues().supportsChildren]);

  useEffect(() => {
    const childrenLiveWithYou = form.getValues()?.childrenLiveWithYou;
    const parentChildren = form.getValues()?.children;

    if (childrenLiveWithYou !== "PARTIALLY" && parentChildren && parentChildren.length > 0) {
      const updatedChildren = parentChildren.map((child: BankCreditRequestChildResponse) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { address, ...rest } = child;
        return rest;
      });
      form.setFieldValue("children", updatedChildren);

      // Also update childrenForm to keep UI in sync
      const currentChildren = childrenForm.values;
      if (currentChildren && Array.isArray(currentChildren) && currentChildren.length > 0) {
        currentChildren.forEach((child, index) => {
          if (child.address) {
            childrenForm.setFieldValue(`${index}.address`, undefined);
          }
        });
      }
    }
  }, [form.getValues()?.childrenLiveWithYou]);

  const updateCredit = useCallback(
    async (values: any, previous: any) => {
      const prevVisibleFields = AppUtils.getVisibleChildrenFields(previous);
      const newVisibleFields = AppUtils.getVisibleChildrenFields(values);
      AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
      AppUtils.updateArrayFieldsWhenVisibilityChanges(
        prevVisibleFields,
        newVisibleFields,
        values,
        childrenForm,
      );

      const tempArray: ChildObject[] = [];
      Object.keys(values).forEach((key) => {
        const item: ChildObject = { ...values[key] };
        const isNotEmpty =
          item.birthdate ||
          item.craft ||
          item.craftAmount ||
          item.firstName ||
          item.lastName ||
          item.address ||
          item.birthdateIncluded !== undefined;
        if (isNotEmpty) {
          item.creditRequestId = credit.id;
          tempArray.push(item);
        }
      });
      form.setFieldValue(propertyName, tempArray);
      setChildrenVisibleFields(newVisibleFields);
    },
    [childrenForm, credit.id, form],
  );

  const minChildBirthDate = useMemo(() => {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 25);
    return minDate;
  }, []);

  const childrenAddressOptions = useMemo(
    () =>
      options["childrenAddress"].map((el) => ({
        value: el,
        label: t(`credit.children.address.options.${el}`),
      })),
    [options, t],
  );

  const transformed = childrenForm.getTransformedValues();
  const length = Object.keys(transformed).length;

  const counts = {
    below6: 0,
    from7to10: 0,
    from11to12: 0,
    above13: 0,
  };

  const alimonyCountsCB = {
    below10: 0,
    aboveAndInclude10: 0,
  };

  Object.values(transformed).forEach((child: any) => {
    if (child.birthdate) {
      const birthdate = new Date(child.birthdate);
      const age = DateUtils.getAgeFromBirthDate(birthdate);

      if (age <= 6) {
        counts.below6++;
      } else if (age >= 7 && age <= 10) {
        counts.from7to10++;
      } else if (age >= 11 && age <= 12) {
        counts.from11to12++;
      } else if (age >= 13) {
        counts.above13++;
      }

      if (age < 10) {
        alimonyCountsCB.below10++;
      } else {
        alimonyCountsCB.aboveAndInclude10++;
      }
    }
  });

  const childrenSummary = [
    { key: "below6", count: counts.below6 },
    { key: "from7to10", count: counts.from7to10 },
    { key: "from11to12", count: counts.from11to12 },
    { key: "above13", count: counts.above13 },
  ];

  const alimonySummary = [
    { key: "below10", count: alimonyCountsCB.below10, amount: 400 },
    { key: "aboveAndInclude10", count: alimonyCountsCB.aboveAndInclude10, amount: 600 },
  ];

  const totalAlimonyAmountCB = useMemo(() => {
    return alimonyCountsCB.below10 * 400 + alimonyCountsCB.aboveAndInclude10 * 600;
  }, [alimonyCountsCB.below10, alimonyCountsCB.aboveAndInclude10]);

  useEffect(() => {
    form.setFieldValue("alimonyAmountCb", totalAlimonyAmountCB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAlimonyAmountCB]);

  return (
    <Box mt="lg">
      {Object.keys(transformed).map((el: any, index: number) => (
        <Box key={el}>
          <SimpleGrid cols={4} mt={"sm"}>
            <Flex align="center" justify="space-between">
              <Checkbox
                label={t("credit.children.section", { number: index + 1 })}
                key={childrenForm.key(`${index}.birthdateIncluded`)}
                {...childrenForm.getInputProps(`${index}.birthdateIncluded`, { type: "checkbox" })}
                disabled={readOnly}
              />
              {length > 0 && !readOnly && (
                <Button
                  variant={"transparent"}
                  size={"compact-sm"}
                  color={"var(--mantine-color-error)"}
                  style={{ display: "flex", alignSelf: "flex-start" }}
                  onClick={() => AppUtils.removeObject(childrenForm, index, undefined)}
                >
                  <IconTrash size={20} />
                </Button>
              )}
            </Flex>
          </SimpleGrid>
          <SimpleGrid cols={4}>
            <DatePicker
              label={t("bankCredit.birthDate.label", { number: index + 1 })}
              key={childrenForm.key(`${index}.birthdate`)}
              {...childrenForm.getInputProps(`${index}.birthdate`)}
              minDate={minChildBirthDate}
              maxDate={new Date()}
              clearable={true}
              disabled={readOnly}
            />

            {form.getValues()?.childrenLiveWithYou === "PARTIALLY" && (
              <Select
                label={t("credit.children.address.label", { number: index + 1 })}
                data={childrenAddressOptions}
                key={childrenForm.key(`${index}.address`)}
                {...childrenForm.getInputProps(`${index}.address`)}
                clearable={true}
                disabled={readOnly}
              />
            )}

            <Box>
              <InputLabel label={t("credit.children.name.label", { number: index + 1 })} />
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    key={childrenForm.key(`${index}.firstName`)}
                    {...childrenForm.getInputProps(`${index}.firstName`)}
                    value={childrenForm.getInputProps(`${index}.firstName`).value}
                    disabled={readOnly}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    key={childrenForm.key(`${index}.lastName`)}
                    {...childrenForm.getInputProps(`${index}.lastName`)}
                    value={childrenForm.getInputProps(`${index}.lastName`).value}
                    disabled={readOnly}
                  />
                </Grid.Col>
              </Grid>
            </Box>

            <YesNoRadioButton
              label={t("credit.children.craft.label")}
              name={`${index}.craft`}
              form={childrenForm}
              disabled={readOnly}
            />

            {childrenVisibleFields.includes(`children.${index}.craftAmount`) && (
              <NumberInput
                label={t("credit.children.craftAmount.label")}
                key={childrenForm.key(`${index}.craftAmount`)}
                {...childrenForm.getInputProps(`${index}.craftAmount`)}
                min={0}
                {...DECIMAL_PROPS}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>
      ))}

      {supportsChildren && length < 4 && !readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(childrenForm, tempChildObject)}
          size={"compact-xs"}
          variant={"transparent"}
          mt={"md"}
        >
          <>
            <IconCirclePlus size={18} style={{ marginRight: 4 }} />
            {t("credit.children.add")}
          </>
        </Button>
      )}

      <SimpleGrid cols={4} mt="lg" mb="md">
        {length > 0 ? (
          <Box>
            <Title order={6} c="var(--mantine-color-gray-7)" mb="sm">
              {t("credit.children.summary.title")}
            </Title>
            {childrenSummary.map((item) => (
              <Text key={item.key} size="sm" c="dimmed">
                {t(`credit.children.summary.${item.key}`)}: {item.count}
              </Text>
            ))}
          </Box>
        ) : (
          <Box />
        )}

        {visibleFields.includes("childrenCaretaker") ? (
          <Select
            label={t("bankCredit.childrenCaretaker.label")}
            data={options["childrenCaretaker"].map((el) => ({
              value: el,
              label: t(`bankCredit.childrenCaretaker.options.${el}`),
            }))}
            key={form.key("childrenCaretaker")}
            {...form.getInputProps("childrenCaretaker")}
            clearable={true}
            disabled={readOnly}
          />
        ) : (
          <Box />
        )}

        {visibleFields.includes("childrenCaretakerFullName") ? (
          <TextInput
            label={t("bankCredit.childrenCaretakerFullName.label")}
            key={form.key("childrenCaretakerFullName")}
            {...form.getInputProps("childrenCaretakerFullName")}
            disabled={readOnly}
          />
        ) : (
          <Box />
        )}

        {length > 0 ? (
          <Box>
            <Title order={6} c="var(--mantine-color-gray-7)" mb="sm">
              {t("credit.children.alimonyAmountCB.label")}:
            </Title>
            {alimonySummary.map((item) => (
              <Text key={item.key} size="sm" c="dimmed">
                {t(`credit.children.alimonyAmountCB.summary.${item.key}`)}: {item.count}x
                {item.amount}CHF
              </Text>
            ))}
          </Box>
        ) : (
          <Box />
        )}
      </SimpleGrid>
    </Box>
  );
};
